// Builds the row of page buttons for the page-navigation directive. The row has exactly `slots` buttons
// (the directive passes however many fit its width) so it never wraps and the buttons do not shift under
// the mouse while paging.
//
// Layouts for 100 pages:
//   page 1-9    [1][2][3][4][5][6][7][8][9][…][100]
//   page 10-16  [1][…][10][11][12][13][14][15][16][…][100]
//   page 92-100 [1][…][92][93][94][95][96][97][98][99][100]
//
// The first and last page are always shown. Pages move through the middle in fixed groups rather than
// sliding one at a time. A gap item ([…]) targets the nearest page it hides, so clicking it advances to
// the next group on that side.
//
// Each item is `{ page }` for a page button, or `{ gap: true, page, from, to }` for a […] button that
// jumps to `page` and stands for pages `from`..`to`.
//
// The fewest slots the layout needs: [1][…][n-1][n][n+1][…][last]. Smaller requests are raised to this.
export const MIN_SLOTS = 7;

export function pageNavigationItems(page, totalPages, slots = 11) {
    const count = Math.max(Math.floor(totalPages) || 0, 0);
    const width = Math.max(Math.floor(slots) || 0, MIN_SLOTS);
    const current = Math.min(Math.max(Math.floor(page) || 1, 1), Math.max(count, 1));

    if (count <= width) {
        return range(1, count);
    }

    const edgeRun = width - 2; // pages shown in a row at the start or end: [...run][…][last]
    const midRun = width - 4;  // pages shown in a row in the middle: [1][…][...run][…][last]

    if (current <= edgeRun) {
        return [...range(1, edgeRun), gap(edgeRun + 1, count - 1, edgeRun + 1), { page: count }];
    }

    const lastRunStart = count - edgeRun + 1;
    if (current >= lastRunStart) {
        return [{ page: 1 }, gap(2, lastRunStart - 1, lastRunStart - 1), ...range(lastRunStart, count)];
    }

    const group = Math.floor((current - edgeRun - 1) / midRun);
    const lo = edgeRun + 1 + group * midRun;
    const hi = lo + midRun - 1;
    return [{ page: 1 }, gap(2, lo - 1, lo - 1), ...range(lo, hi), gap(hi + 1, count - 1, hi + 1), { page: count }];
}

// The largest slot count whose row for `page` fits in `available`: the row each count would produce is
// priced with `widthOf(item)` and the widest that fits is kept. Never fewer than MIN_SLOTS, never more
// than the pages (a row with every page needs no gaps).
export function slotsThatFit(page, totalPages, available, widthOf) {
    const count = Math.max(Math.floor(totalPages) || 0, 1);
    const rowWidth = (slots) => pageNavigationItems(page, count, slots)
        .reduce((sum, item) => sum + widthOf(item), 0);

    let fitted = MIN_SLOTS;
    for (let slots = MIN_SLOTS + 1; slots <= count && rowWidth(slots) <= available; slots++) {
        fitted = slots;
    }
    return fitted;
}

function range(from, to) {
    const items = [];
    for (let p = from; p <= to; p++) {
        items.push({ page: p });
    }
    return items;
}

function gap(from, to, target) {
    return { gap: true, page: target, from: from, to: to };
}
